import api from '@/services/api';
import type {
    AccessJustification,
    AccessJustificationPayload,
    AccessStatus,
    CallerAuthorisedContact,
    CallerConfirmationDetails,
    CallerConfirmationPayload,
    CallerConfirmationResult,
} from '../types/agreementAccess.types';

type RawNode = Record<string, unknown>;

const isRecord = (value: unknown): value is RawNode =>
    typeof value === 'object' && value !== null && !Array.isArray(value);

const justificationsPath = (agreementId: string) => `/agreements/${agreementId}/access-justifications`;
const confirmationsPath = (agreementId: string) => `/agreements/${agreementId}/access-confirmations`;

const heldDetail = (value: unknown): string | null => (typeof value === 'string' && value.trim() !== '' ? value : null);

const mapAuthorisedContacts = (raw: unknown): CallerAuthorisedContact[] => {
    if (!Array.isArray(raw)) return [];

    return raw.flatMap((entry) => {
        const name = isRecord(entry) ? heldDetail(entry.name) : null;
        if (!isRecord(entry) || name === null) return [];

        return [{ name, relationship: typeof entry.relationship === 'string' ? entry.relationship : '' }];
    });
};

const mapJustification = (raw: RawNode): AccessJustification => ({
    id: String(raw.id ?? ''),
    agreementId: String(raw.agreement_id ?? ''),
    caseReference: String(raw.case_reference ?? ''),
    reason: String(raw.reason ?? ''),
    grantedAt: String(raw.granted_at ?? ''),
    expiresAt: String(raw.expires_at ?? ''),
});

export const fetchCurrentJustification = async (agreementId: string): Promise<AccessStatus> => {
    const raw: unknown = (await api.get(`${justificationsPath(agreementId)}/current`)).data?.data;
    const data = isRecord(raw) ? raw : {};

    return {
        // Anything but a literal false prompts, matching the direction the gate fails in.
        required: data.required !== false,
        justification: isRecord(data.justification) ? mapJustification(data.justification) : null,
        // Anything but a literal true is unconfirmed, matching the direction the gate fails in.
        confirmed: data.confirmed === true,
    };
};

export const recordJustification = async (
    agreementId: string,
    payload: AccessJustificationPayload,
): Promise<AccessJustification> => {
    const response = await api.post(justificationsPath(agreementId), {
        case_reference: payload.caseReference,
        reason: payload.reason,
    });

    return mapJustification(response.data.data);
};

export const fetchConfirmationDetails = async (agreementId: string): Promise<CallerConfirmationDetails> => {
    const raw: unknown = (await api.get(`${confirmationsPath(agreementId)}/details`)).data?.data;
    const data = isRecord(raw) ? raw : {};

    return {
        agreementRef: heldDetail(data.agreement_ref),
        fullName: heldDetail(data.full_name),
        addressLine1: heldDetail(data.address_line_1),
        postcode: heldDetail(data.postcode),
        authorisedContacts: mapAuthorisedContacts(data.authorised_contacts),
    };
};

export const recordConfirmation = async (
    agreementId: string,
    payload: CallerConfirmationPayload,
): Promise<CallerConfirmationResult> => {
    const response = await api.post(confirmationsPath(agreementId), {
        name_confirmed: payload.nameConfirmed,
        address_confirmed: payload.addressConfirmed,
        postcode_confirmed: payload.postcodeConfirmed,
    });
    const raw: unknown = response.data?.data;
    const data = isRecord(raw) ? raw : {};

    return {
        // Anything but a literal passed is a failure: the Agreement stays closed.
        outcome: data.outcome === 'passed' ? 'passed' : 'failed',
        nameConfirmed: data.name_confirmed === true,
        addressConfirmed: data.address_confirmed === true,
        postcodeConfirmed: data.postcode_confirmed === true,
    };
};
